import jwtDecode from 'jwt-decode';

const JWT_COOKIE_NAME = 'jwt';

/**
 * Retrieve the JWT from cookies (for server-side) or localStorage (for client-side).
 * @param {Object} app - The Nuxt app instance (provides access to $cookies).
 * @returns {string|null} The JWT token, or null if not available.
 */
export function getJwt(app) {
  if (process.server) {
    return app.$cookies?.get(JWT_COOKIE_NAME) || null;
  }
  if (process.client) {
    return localStorage.getItem(JWT_COOKIE_NAME) || null;
  }
  return null;
}

/**
 * Validate a JWT to check its format, and optionally verify expiration.
 * @param {string} token - The JWT token to validate.
 * @returns {boolean} True if the token is valid, false otherwise.
 */
export function isValidJwt(token) {
  if (!token) return false;

  try {
    // Decode the token to validate its structure and check expiration
    const decoded = jwtDecode(token);

    // Optional: Check if the token is expired
    if (decoded.exp && Date.now() >= decoded.exp * 1000) {
      console.warn('[JWT] Token is expired.');
      return false;
    }

    return true;
  } catch (error) {
    console.error('[JWT] Invalid token:', error.message);
    return false;
  }
}

/**
 * Decode and return the payload of a JWT.
 * @param {string} token - The JWT token to decode.
 * @returns {Object|null} The decoded payload, or null if the token is invalid.
 */
export function decodeJwt(token) {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('[JWT] Failed to decode token:', error.message);
    return null;
  }
}

/**
 * Remove the JWT from cookies or localStorage depending on the environment.
 * @param {Object} app - The Nuxt app instance (provides access to $cookies).
 */
export function removeJwt(app) {
  const domain = '.' + app.$config.DOMAIN; // Ensure the same domain as setup

  // Remove the token from cookies (applies to both server and client)
  app.$cookies?.remove('jwt', { path: '/', domain });

  // Remove the token from localStorage (applies only to client-side)
  if (process.client) {
    localStorage.removeItem('jwt');
  }
}
